import cn from 'classnames';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  DEFAULT_MEDICAL_IMAGE_HEIGHT,
  DEFAULT_MEDICAL_IMAGE_HEIGHT_MOBILE,
} from '@/shared/config';
import type { ToothBasisSeriesName } from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { useMedia } from '@/shared/hooks';

import { medicalImageTitles } from '@/entities/assets';

import type {
  FullHeadMprSliceGroupView,
  FullHeadMprSliceRecordViewsByTileID,
  FullHeadMprSliceTileView,
  ToothSliceOptions,
} from '../../config';
import { makeFullHeadMprSliceGuides } from '../../lib';
import { FullHeadMprSliceGuides } from '../FullHeadMprSliceGuides';
import { FullHeadMprSliceOptionsControls } from '../FullHeadMprSliceOptionsControls';
import { FullHeadMprSlicesLoadingPlaceholder } from '../FullHeadMprSlicesLoadingPlaceholder';
import { FullHeadMprSliceTileList } from '../FullHeadMprSliceTileList';

import styles from './FullHeadMprSliceGroupSection.module.scss';

type FullHeadMprSliceGroupSectionProps = {
  canChangeOptions: boolean;
  group: FullHeadMprSliceGroupView;
  groups: FullHeadMprSliceGroupView[];
  pendingTileIDs: string[];
  recordsByTileID: FullHeadMprSliceRecordViewsByTileID;
  thicknessOptionsMm: number[];
  className?: string;
  isTileSelected: (tile: FullHeadMprSliceTileView) => boolean;
  onOptionsChange: (
    seriesName: ToothBasisSeriesName,
    options: ToothSliceOptions,
  ) => void;
  onToggleTile: (tile: FullHeadMprSliceTileView) => void;
  onZoomTile: (tileID: string) => void;
};

export const FullHeadMprSliceGroupSection: FC<
  FullHeadMprSliceGroupSectionProps
> = (props) => {
  const {
    canChangeOptions,
    group,
    groups,
    pendingTileIDs,
    recordsByTileID,
    thicknessOptionsMm,
    className,
    isTileSelected,
    onOptionsChange,
    onToggleTile,
    onZoomTile,
  } = props;

  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();

  const [hoveredTileID, setHoveredTileID] = useState('');

  const guides = makeFullHeadMprSliceGuides({ group, groups });

  return (
    <div className={cn(className)} data-section="mpr-slice-group">
      <div className={styles.sliceWrapper}>
        <h4 className={isMobile ? 'h5' : 'h4'}>
          {formatMessage(medicalImageTitles[group.groupName])}
        </h4>
        <FullHeadMprSliceOptionsControls
          className={styles.sliceConfigsWrapper}
          disabled={!canChangeOptions}
          options={group.options}
          thicknessOptionsMm={thicknessOptionsMm}
          onChange={(options) => onOptionsChange(group.seriesName, options)}
        />
      </div>

      <div className={styles.medicalImageSection}>
        {!isMobile && guides.length > 0 && (
          <FullHeadMprSliceGuides
            className={styles.guidesWrapper}
            guides={guides}
            hoveredTileID={hoveredTileID}
            onHoveredTileChanged={setHoveredTileID}
            onLineClick={onZoomTile}
          />
        )}

        {group.isPending ? (
          <FullHeadMprSlicesLoadingPlaceholder
            height={
              isMobile
                ? DEFAULT_MEDICAL_IMAGE_HEIGHT_MOBILE
                : DEFAULT_MEDICAL_IMAGE_HEIGHT
            }
          />
        ) : (
          <FullHeadMprSliceTileList
            group={group}
            hoveredTileID={hoveredTileID}
            pendingTileIDs={pendingTileIDs}
            recordsByTileID={recordsByTileID}
            isTileSelected={isTileSelected}
            onHoveredTileChanged={setHoveredTileID}
            onToggleTile={onToggleTile}
            onZoomTile={onZoomTile}
          />
        )}
      </div>
    </div>
  );
};
